/**
 * One extracted photo item with independent Add task / Add event actions.
 * Both stay visible after use so the user can add the item both ways.
 */

import { Ionicons } from '@expo/vector-icons';
import { useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { PhotoActionItem } from '@/types/photo-actions';

interface PhotoActionResultCardProps {
  item: PhotoActionItem;
  onAddTask: (item: PhotoActionItem) => Promise<void>;
  onAddEvent: (item: PhotoActionItem) => void;
}

export function PhotoActionResultCard({ item, onAddTask, onAddEvent }: PhotoActionResultCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [taskState, setTaskState] = useState<'idle' | 'adding' | 'added'>('idle');

  const handleAddTask = async () => {
    if (taskState !== 'idle') return;
    setTaskState('adding');
    try {
      await onAddTask(item);
      setTaskState('added');
    } catch {
      setTaskState('idle');
    }
  };

  const dateLine = [item.date, item.time].filter(Boolean).join(' · ');

  return (
    <View style={[styles.card, { backgroundColor: palette.cardBackground, borderColor: palette.borderMuted }]}>
      <Text style={[styles.title, { color: palette.text, fontFamily: getBodyFont() }]}>{item.title}</Text>
      {dateLine.length > 0 && (
        <Text style={[styles.meta, { color: palette.textMuted, fontFamily: getBodyFont() }]}>{dateLine}</Text>
      )}
      {item.location ? (
        <Text style={[styles.meta, { color: palette.textMuted, fontFamily: getBodyFont() }]}>📍 {item.location}</Text>
      ) : null}
      {item.confidence && item.confidence !== 'high' && (
        <Text style={[styles.confidence, { color: palette.warning, fontFamily: getBodyFont() }]}>
          {item.confidence} confidence — double-check the details
        </Text>
      )}
      <View style={styles.buttonsRow}>
        <TouchableOpacity
          style={[styles.pill, { backgroundColor: taskState === 'idle' ? palette.primary : '#888888' }]}
          onPress={handleAddTask}
          disabled={taskState !== 'idle'}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Add as task"
          accessibilityState={{ disabled: taskState !== 'idle' }}
        >
          <Ionicons
            name={taskState === 'added' ? 'checkmark' : 'checkmark-circle-outline'}
            size={14}
            color="#FFFFFF"
          />
          <Text style={styles.pillText}>
            {taskState === 'added' ? 'Task added' : taskState === 'adding' ? 'Adding…' : 'Add task'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.pill, { backgroundColor: palette.primary }]}
          onPress={() => onAddEvent(item)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Add as event"
        >
          <Ionicons name="calendar-outline" size={14} color="#FFFFFF" />
          <Text style={styles.pillText}>Add event</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: BorderRadius.md,
    borderWidth: 1,
    padding: Spacing.lg,
    gap: Spacing.xs,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
  },
  meta: {
    fontSize: 13,
  },
  confidence: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  buttonsRow: {
    flexDirection: 'row',
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderRadius: BorderRadius.xl,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  pillText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
  },
});
